import Link from 'next/link';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';
import Image from '@/components/image';
import { getFallbackImage } from '@/utils/images';

/*
 * The logo sits on the brand's own background colour, which is the same in
 * both themes (logos are drawn for it). White is the fallback for the same
 * reason: brand artwork assumes a light backing.
 */
const LogoTile = styled.div`
  align-items: center;
  aspect-ratio: 5 / 3;
  background-color: ${(props) => props.$backgroundColor || '#ffffff'};
  box-sizing: border-box;
  content-visibility: auto;
  contain-intrinsic-size: auto 120px;
  display: flex;
  justify-content: center;
  /* Keep the 5:3 box even when the logo is taller than it. */
  min-height: 0;
  padding: 16px;
  pointer-events: none;
  width: 100%;

  > picture {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    max-height: 150px;
    min-height: 0;
    width: 100%;
  }

  > picture img {
    height: auto;
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
    width: auto;
  }
`;

// Create styled component at module level - no need for dynamic import
const BrandLogo = styled(Image).attrs({ unoptimized: true })`
  max-height: 150px;
  max-width: 100%;
`;

const BrandName = styled.p`
  border-top: 1px solid var(--border);
  color: var(--card-foreground);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  margin: 0;
  overflow: hidden;
  padding: 10px 12px;
  text-align: center;
  text-overflow: ellipsis;
  transition: color 150ms;
  white-space: nowrap;

  [class*="dapp__Wrapper"] & {
    display: none;
  }
`;

const CardWrapper = styled.div`
  background-color: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  position: relative;
  transition:
    border-color 250ms ease,
    box-shadow 250ms ease,
    transform 250ms ease;
  width: 100%;
  z-index: 1;

  @media (hover: hover) {
    &:hover {
      border-color: color-mix(in oklab, var(--primary) 50%, transparent);
      box-shadow: var(--shadow-lg);
      transform: translateY(-4px);
      z-index: 2;

      ${BrandName} {
        color: var(--primary);
      }
    }
  }

  a:focus-visible > & {
    border-color: var(--ring);
    box-shadow: 0 0 0 3px color-mix(in oklab, var(--ring) 50%, transparent);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const CardLink = styled(Link)`
  border-radius: var(--radius-xl);
  display: block;
  text-decoration: none;
  width: 100%;

  &:focus-visible {
    outline: none;
  }
`;

const urlForBrand = (brand) => {
  const countrySlug = brand.countrySlug || brand.country.slug;
  return `/brands/${countrySlug}/${brand.slug}`;
};

const BrandCard = ({ brand, hideName = false, className, style, onClick, priority = false }) => {
  const t = useTranslations('brandGrid');

  if (!brand) {
    return null;
  }

  const logo = (
    <LogoTile $backgroundColor={brand.backgroundColor}>
      <BrandLogo
        src={brand.imageUrl}
        width={250}
        height={150}
        alt={t('logoAlt', { brand: brand.name })}
        fallback={getFallbackImage(brand.type)}
        priority={priority}
      />
    </LogoTile>
  );

  const handleClick = () => {
    if (typeof onClick === 'function') {
      onClick(brand);
    }
  };

  if (typeof onClick === 'function') {
    return (
      <CardWrapper onClick={handleClick} style={style} className={className}>
        {logo}
        {!hideName && <BrandName>{brand.name}</BrandName>}
      </CardWrapper>
    );
  }

  // Only gift cards are called gift cards: the grid also lists top-ups and vouchers.
  const title =
    brand.type === 'giftcard'
      ? t('cardTitle', { brand: brand.name })
      : t('cardTitleOther', { brand: brand.name });

  return (
    <CardLink
      key={brand.id}
      href={urlForBrand(brand)}
      prefetch={false}
      title={title}
      style={style}
      className={className}
      onClick={onClick}
    >
      <CardWrapper>
        {logo}
        {!hideName && <BrandName>{brand.name}</BrandName>}
      </CardWrapper>
    </CardLink>
  );
};

export default BrandCard;
